import { useMemo } from 'react'
import ReactECharts from 'echarts-for-react'

export interface SankeyNode {
  /** Unique identifier — also used as the ECharts node key. */
  name: string
  /** Human-readable label shown inside the node block. Falls back to `name` if omitted. */
  displayLabel?: string
  /** Column index for stage alignment (ECharts `depth`). */
  depth?: number
  /** Node fill colour. */
  itemStyle?: { color: string }
  /** Tooltip extra data. */
  count?: number
}

export interface SankeyLink {
  source: string
  target: string
  value: number
}

export interface SankeyChartProps {
  data: SankeyNode[]
  links: SankeyLink[]
  /** Minimum chart height in px. The chart grows taller as needed when there are many nodes. Default 450. */
  height?: number
}

/** Longest-path depth per node (DAG). */
function computeLayerDepths(
  nodeNames: readonly string[],
  links: ReadonlyArray<{ source: string; target: string }>
): Map<string, number> {
  const names = new Set(nodeNames)
  const depth = new Map<string, number>()
  for (const n of names) depth.set(n, 0)

  const edges = links.filter(l => names.has(l.source) && names.has(l.target))
  const maxIter = Math.max(names.size, edges.length) + 2
  for (let i = 0; i < maxIter; i++) {
    let changed = false
    for (const { source, target } of edges) {
      const nd = (depth.get(source) ?? 0) + 1
      if (nd > (depth.get(target) ?? 0)) {
        depth.set(target, nd)
        changed = true
      }
    }
    if (!changed) break
  }
  return depth
}

function sankeyLayoutMetrics(
  nodeNames: string[],
  links: ReadonlyArray<{ source: string; target: string }>
): { maxPerLayer: number } {
  if (nodeNames.length === 0) return { maxPerLayer: 1 }

  const depth = computeLayerDepths(nodeNames, links)
  const counts = new Map<number, number>()
  for (const n of nodeNames) {
    const d = depth.get(n) ?? 0
    counts.set(d, (counts.get(d) ?? 0) + 1)
  }
  let maxPerLayer = 0
  for (const c of counts.values()) maxPerLayer = Math.max(maxPerLayer, c)
  return { maxPerLayer: Math.max(maxPerLayer, 1) }
}

const ROW_UNIT   = 46   // more breathing room per node row
const NODE_WIDTH = 80   // wide enough for ~10–12 chars at font-size 11

const SankeyChart = ({ data, links, height = 450 }: SankeyChartProps) => {
  const nodeNames = useMemo(() => data.map(n => n.name), [data])

  const chartHeight = useMemo(() => {
    const { maxPerLayer } = sankeyLayoutMetrics(nodeNames, links)
    return Math.max(height, 72 + maxPerLayer * ROW_UNIT)
  }, [nodeNames, links, height])

  const nodes = data.map((n) => ({
    name:      n.name,
    depth:     n.depth,
    itemStyle: n.itemStyle,
    label:     { formatter: n.displayLabel ?? n.name },
  }))

  const options = {
    tooltip: {
      trigger: 'item',
      triggerOn: 'mousemove',
      formatter: (params: any) => {
        if (params.dataType === 'node') {
          const node = data.find(n => n.name === params.name)
          const label = node?.displayLabel ?? params.name
          const count = node?.count ?? params.value
          return `<strong>${label}</strong><br/>${count?.toLocaleString() ?? ''} conversions`
        }
        const srcNode = data.find(n => n.name === params.data.source)
        const tgtNode = data.find(n => n.name === params.data.target)
        const src = srcNode?.displayLabel ?? params.data.source
        const tgt = tgtNode?.displayLabel ?? params.data.target
        return `${src} → ${tgt}<br/><strong>${params.value?.toLocaleString()}</strong> conversions`
      },
    },
    animation: true,
    series: [
      {
        type: 'sankey',
        data: nodes,
        links,
        bottom: '2%',
        left:   '1%',
        right:  '1%',
        top:    '2%',
        nodeWidth: NODE_WIDTH,
        nodeGap:   10,
        emphasis: { focus: 'adjacency' },
        orient: 'horizontal',
        nodeAlign: 'left',
        label: {
          position:   'inside',
          fontSize:   11,
          fontWeight: 500,
          color:      '#fff',
          overflow:   'truncate',
          width:      NODE_WIDTH - 10,
        },
        lineStyle: {
          color:     'gradient',
          curveness: 0.5,
          opacity:   0.35,
        },
      },
    ],
  }

  return (
    <ReactECharts
      option={options}
      style={{ width: '100%', height: `${chartHeight}px` }}
      opts={{ renderer: 'svg' }}
    />
  )
}

export type { SankeyChartProps as LineChartProps }
export default SankeyChart
